/**
 * Zentrale Plan-Definition (Limits + Preise).
 * Single source of truth für Frontend + Backend.
 */

export const PLAN_ORDER = ["free", "standard", "pro", "elite", "ultimate", "dev"];

export const PLAN_LABELS = {
  free: "Free",
  standard: "Standard",
  pro: "Pro",
  elite: "Elite",
  ultimate: "Ultimate",
  dev: "Dev",
};

const PLAN_DEFS = {
  free: {
    id: "free",
    txLimit: 100,
    prices: { earlyAdopter: 0, standard: 0 },
  },
  standard: {
    id: "standard",
    txLimit: 1000,
    prices: { earlyAdopter: 39.99, standard: 59.99 },
  },
  pro: {
    id: "pro",
    txLimit: 3000,
    prices: { earlyAdopter: 59.99, standard: 79.99 },
  },
  elite: {
    id: "elite",
    txLimit: 10000,
    prices: { earlyAdopter: 79.99, standard: 99.99 },
  },
  ultimate: {
    id: "ultimate",
    txLimit: null,
    unlimited: true,
    prices: { earlyAdopter: 109.99, standard: 129.99 },
  },
  dev: {
    id: "dev",
    txLimit: null,
    unlimited: true,
    prices: { earlyAdopter: 0, standard: 0 },
  },
};

/** Early-Adopter-Preise aktiv (Default: an). Env: EARLY_ADOPTER_PRICING=0 zum Umschalten. */
export function isEarlyAdopterPricingActive(env = process.env) {
  const raw = env?.EARLY_ADOPTER_PRICING ?? env?.VITE_EARLY_ADOPTER_PRICING ?? "1";
  return String(raw).trim() !== "0";
}

export function normalizePlanId(raw) {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (!s) return "free";
  if (s === "dev" || s.includes("dev") || s.includes("admin")) return "dev";
  if (s.includes("ultimate")) return "ultimate";
  if (s.includes("elite")) return "elite";
  if (s.includes("pro")) return "pro";
  if (s.includes("standard")) return "standard";
  if (s.includes("free")) return "free";
  return s;
}

export function formatPlanLabel(plan) {
  const id = normalizePlanId(plan);
  return PLAN_LABELS[id] || (id ? id.charAt(0).toUpperCase() + id.slice(1) : "Free");
}

export function isUnlimitedPlan(plan) {
  const id = normalizePlanId(plan);
  const def = PLAN_DEFS[id];
  return def?.unlimited === true || def?.txLimit == null;
}

/** null = unlimited */
export function getPlanTxLimit(plan) {
  const id = normalizePlanId(plan);
  const def = PLAN_DEFS[id];
  if (!def) return 100;
  if (def.unlimited || def.txLimit == null) return null;
  return Number(def.txLimit);
}

export function getPlanPriceEUR(plan, options = {}) {
  const id = normalizePlanId(plan);
  const def = PLAN_DEFS[id];
  if (!def) return 0;
  const early = options.earlyAdopter ?? isEarlyAdopterPricingActive(options.env);
  return Number(early ? def.prices.earlyAdopter : def.prices.standard) || 0;
}

const UPGRADE_STEP_EA = {
  "free->standard": 39.99,
  "standard->pro": 20,
  "pro->elite": 20,
  "elite->ultimate": 30,
};

const UPGRADE_STEP_STANDARD = {
  "free->standard": 59.99,
  "standard->pro": 20,
  "pro->elite": 20,
  "elite->ultimate": 30,
};

export function getUpgradePriceEUR(fromPlan, toPlan, options = {}) {
  const from = normalizePlanId(fromPlan);
  const to = normalizePlanId(toPlan);
  if (from === to) return 0;

  const fromIdx = PLAN_ORDER.indexOf(from);
  const toIdx = PLAN_ORDER.indexOf(to);
  if (fromIdx === -1 || toIdx === -1 || toIdx <= fromIdx) return 0;

  const early = options.earlyAdopter ?? isEarlyAdopterPricingActive(options.env);
  const steps = early ? UPGRADE_STEP_EA : UPGRADE_STEP_STANDARD;

  let sum = 0;
  for (let i = fromIdx; i < toIdx; i++) {
    const key = `${PLAN_ORDER[i]}->${PLAN_ORDER[i + 1]}`;
    const step = steps[key];
    if (step == null) {
      throw new Error(`Missing upgrade step price: ${key}`);
    }
    sum += step;
  }

  return Number(sum.toFixed(2));
}

export function getPlanInfo(plan, options = {}) {
  const id = normalizePlanId(plan);
  const def = PLAN_DEFS[id];
  if (!def) return null;
  const txLimit = getPlanTxLimit(id);
  const price = getPlanPriceEUR(id, options);
  return {
    id,
    name: formatPlanLabel(id),
    transactionLimit: txLimit,
    unlimited: txLimit == null,
    price,
    priceEUR: price,
  };
}

/** Effektives Limit: Ultimate/Dev unlimited; sonst mindestens Katalog (keine veralteten Caps). */
export function resolvePlanTxLimit(effectivePlan, storedLimit) {
  if (isUnlimitedPlan(effectivePlan)) return null;
  const catalogLimit = getPlanTxLimit(effectivePlan);
  if (catalogLimit == null) return null;
  const stored = Number(storedLimit);
  if (Number.isFinite(stored) && stored > 0) {
    return Math.max(stored, catalogLimit);
  }
  return catalogLimit;
}

export function exceedsTxPlanLimit(used, effectivePlan, storedLimit) {
  const limit = resolvePlanTxLimit(effectivePlan, storedLimit);
  if (limit == null) return false;
  const u = Number(used);
  if (!Number.isFinite(u) || u < 0) return false;
  return u > limit;
}

export function getPlanDiscountPercent(plan, options = {}) {
  const launch = getPlanPriceEUR(plan, { ...options, earlyAdopter: true });
  const regular = getPlanPriceEUR(plan, { ...options, earlyAdopter: false });
  if (!Number.isFinite(regular) || regular <= 0 || launch >= regular) return 0;
  return Math.round(((regular - launch) / regular) * 100);
}

export function getPlanSavingsEUR(plan, options = {}) {
  const launch = getPlanPriceEUR(plan, { ...options, earlyAdopter: true });
  const regular = getPlanPriceEUR(plan, { ...options, earlyAdopter: false });
  if (!Number.isFinite(regular) || regular <= launch) return 0;
  return Number((regular - launch).toFixed(2));
}

export function formatTxLimitDisplay(plan, language = "de") {
  if (isUnlimitedPlan(plan)) {
    return language === "en" ? "Unlimited" : "Unbegrenzt";
  }
  const limit = getPlanTxLimit(plan);
  return limit != null ? String(limit) : "—";
}

export const PLAN_PRICES_TITLECASE = Object.fromEntries(
  PLAN_ORDER.filter((id) => id !== "dev").map((id) => [formatPlanLabel(id), getPlanPriceEUR(id, { earlyAdopter: false })])
);

export const PLAN_LIMITS_BY_TITLECASE = Object.fromEntries(
  PLAN_ORDER.filter((id) => id !== "dev").map((id) => {
    const limit = getPlanTxLimit(id);
    return [formatPlanLabel(id), limit == null ? null : limit];
  })
);
